/*
    Copyright 2023 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { inspect } from "util";
import type { SpanAttributes } from "@opentelemetry/api";
import type { Context, HttpRequest as HttpRequestV3 } from "@azure/functions-v3";
import { HttpRequest } from "@azure/functions";
import { SemConvTrace, getHttpHeadersToCapture } from "@dynatrace/opentelemetry-core";

import { getHttpOutBindingName } from "./Bindings";
import { logger } from "./Logger";

// ============================================================================

// eslint-disable-next-line deprecation/deprecation -- Api 1.0.0 compat
type Attributes = SpanAttributes;

// Interface describing the Http response parts relevant for us as interface is not
// yet included yet in @azure/functions
interface HttpResponse {
	statusCode?: number | string;
	status?: number | string | (() => unknown);
}

// ============================================================================
/**
 * Returns span attributes from captured http headers (e.g. referer, user-agent, etc.. )
 * @param req the azure function HttpRequest request object
 */
function captureHttpHeaders(req: HttpRequest | HttpRequestV3): Attributes {
	const attrs: Attributes = {};
	const headersToCapture = getHttpHeadersToCapture();
	for (const [headerName, semConvKey] of Object.entries(headersToCapture)) {
		const headerValue = headerGetter(req, headerName);
		if (headerValue != null) {
			attrs[semConvKey] = headerValue;
		}
	}

	const forwarded = headerGetter(req, "forwarded");
	if (forwarded != null) {
		attrs[SemConvTrace.DT_HTTP_REQUEST_HEADER_FORWARDED] = forwarded;
	} else {
		const xForwardedFor = headerGetter(req, "x-forwarded-for");
		if (xForwardedFor != null) {
			attrs[SemConvTrace.DT_HTTP_REQUEST_HEADER_X_FORWARDED_FOR] = xForwardedFor;
		}
	}

	return attrs;
}

// ============================================================================
/**
 * Helper method to get header values from two different request types used by @azure/functions v3 and v4.
 * @param req Http request object with headers
 * @param headerName name of the header to look for
 * @returns header value or null
 */
function headerGetter(req: HttpRequest | HttpRequestV3, headerName: string ): string | null {
	if (req instanceof HttpRequest) {
		return req.headers.get(headerName);
	} else {
		return req.headers[headerName];
	}
}

// ============================================================================
/**
 * Get Span start attributes depending on the given trigger type.
 *
 * @note currently only Http trigger is supported
 *
 * @param triggerType the azure function trigger type
 * @param context the azure function context
 * @param args the arguments passed to the function
 * @returns the detected span start attributes
 */
export function getStartAttributes(triggerType: SemConvTrace.FaasTriggerValues, req?: HttpRequest | HttpRequestV3): Attributes {
	let attributes: Attributes;
	if (triggerType === SemConvTrace.FaasTriggerValues.HTTP && req != null) {
		attributes = {
			[SemConvTrace.FAAS_TRIGGER]: triggerType,
			[SemConvTrace.HTTP_METHOD]: req.method || "GET",
			[SemConvTrace.HTTP_URL]: req.url,
			...captureHttpHeaders(req)
		};
	} else {
		attributes = {
			[SemConvTrace.FAAS_TRIGGER]: triggerType
		};
	}

	if (logger.debugEnabled) {
		logger.debug(`startAttributes: ${inspect(attributes)}`);
	}

	return attributes;
}

// ----------------------------------------------------------------------------
/**
 * Get Span exit attributes depending on the given trigger type.
 *
 * @note currently only Http trigger is supported
 *
 * @param triggerType the trigger type
 * @param context the azure function context
 * @param retVal the retrun value of the handler
 * @returns the detected span exit attributes
 */
export function getExitAttributes(triggerType: SemConvTrace.FaasTriggerValues, context: Context, retVal: unknown): Attributes {
	let attributes: Attributes = {};

	if (triggerType === SemConvTrace.FaasTriggerValues.HTTP) {
		const name = getHttpOutBindingName(context);
		if (name != null) {
			let httpResponse: HttpResponse | undefined;
			if (name === "$return") {
				httpResponse = retVal as HttpResponse;
			} else {
				if (context.res != null && context.bindings[name] == null) {
					httpResponse = context.res;
				} else {
					httpResponse = context.bindings[name];
				}
			}
			if (httpResponse != null) {
				// statusCode field is preferred, use status only if it is no function/setter
				let httpStatus = httpResponse.statusCode;
				if (httpStatus == null && typeof(httpResponse.status) !== "function") {
					httpStatus = httpResponse.status;
				}
				// no status given defaults to 200
				httpStatus = +(httpStatus ?? 200);
				if (!isNaN(httpStatus)) {
					attributes = {
						[SemConvTrace.HTTP_STATUS_CODE]: httpStatus
					};
				}
			} else {
				// if no response object is given Azure defaults to 204
				attributes = {
					[SemConvTrace.HTTP_STATUS_CODE]: 204
				};
			}
		} else {
			logger.info("can't get HTTP out binding name");
		}
	}

	if (logger.debugEnabled) {
		logger.debug(`exitAttributes: ${inspect(attributes)}`);
	}

	return attributes;
}
